import React from "react"
import { useNavigate } from "react-router-dom"
import FormElement from "components/Form/FormElement"
import * as UserApi from "features/api/UserApi"

import { useAlert } from "hooks/useAlert"
import { useTranslation } from "react-i18next"
import { isConfirmationCodeValid, isEmailValid } from "utils/validation"
import { publicRoutes } from "router/routes"
import { BC_OPERATOR } from "utils/constants"
import { useSubdomain } from "hooks/useSubdomain"

const defaultFormFields = {
    email: "",
    code: "",
    password: "",
    passwordConfirmation: ""
}

const defaultFormEvents = {
    ready: true
}

export const FormForgotPassword = () => {
    const [formFields, setFormFields] = React.useState(defaultFormFields)
    const [formEvents, setFormEvents] = React.useState(defaultFormEvents)
    const [btnDisabled, setBtnDisabled] = React.useState(false)

    const [errors, setErrors] = React.useState([])
    const [showLocalErrors, setShowLocalErrors] = React.useState(false)
    const [showGlobalError, setShowGlobalError] = React.useState(false)
    const { currentSubdomain } = useSubdomain()

    const { alert } = useAlert()
    const { t } = useTranslation()
	const navigate = useNavigate()

    const handleInputChange = (event) => {
        const target = event.target
        const value = target.type === "checkbox" ? target.checked : target.value
        const name = target.name

        setFormFields({
            ...formFields,
            [name]: value
        })
    }

    const handleValidation = React.useCallback((name, errs) => {
        setErrors((s) => {
            const cleanErrors = [...s.filter((e) => e.origin !== name)]
            return [
                ...cleanErrors,
                ...errs
            ]
        })
    }, [])

    const handleSubmit = async (event) => {
        event.preventDefault()
        // Validate inputs
        const formReady = !(errors.length > 0)

        if (!formReady) {
            setShowLocalErrors(true)
        }
        else {

            try {
                // hide error
                setShowLocalErrors(false)
                // prevent form to be submitted again
                setFormEvents({
                    ...formEvents,
                    ready: false
                })

                const { getError } = await UserApi.updatePassword(formFields.code.trim(), formFields.password.trim(), formFields.email.trim())
                if (getError()) throw getError()
                alert("success", t("updatePassword_success", { ns: "auth" }))
                // Redirect to login view
                navigate(publicRoutes.login.path)
                    
            } catch (error) {
                console.error(error)
                const erroMsg = error?.message?.toString()
                if (erroMsg) alert("error", erroMsg)
                setShowGlobalError(true)
            } finally {
                setFormEvents(s => ({
                    ...s,
                    ready: true
                }))
            }
        }
    }

    const initPasswordWithBackend = async () => {
        try {
            setBtnDisabled(true)
            const template_mail = (currentSubdomain.id_api === "OC") ? BC_OPERATOR : currentSubdomain.id_api
            const { getError } = await UserApi.initPassword(formFields.email.trim(), template_mail)
            if (getError()) throw getError()
            alert("success", t("mail_update_reset_success", { ns: "form" }))
        } catch (error) {
            console.error(error)
            const erroMsg = error?.message?.toString()
            if (erroMsg) alert("error", erroMsg)
        } finally {
            setBtnDisabled(false)
        }
    }

    return (
        <form data-cy="form-forgot-password">
            {showGlobalError &&
                <p className="text-xs text-danger mb-5">{t("updatePassword_global_error", { ns: "auth" })}</p>
            }
            {/* Email */}
            <FormElement
                value={formFields.email}
                name="email"
                type="email"
                label={t("mail", { ns: "auth" })}
                className="mb-1"
                classNameLabel="text-primary"
                initPassword={true}
                required={true}
                showErrors={showLocalErrors}
                onValidate={handleValidation}
                onChange={handleInputChange}
            />

            <div className="flex justify-end">
                <button 
                    type="button" 
                    className="c-btn c-btn--secondary text-xs u-br-base px-5 mb-7" 
                    disabled={btnDisabled || !isEmailValid(formFields.email)} 
                    onClick={initPasswordWithBackend}>
                    {t("global.send")}
                </button>
            </div>

            {/* Code */}
            <FormElement
                value={formFields.code}
                name="code"
                type="text"
                label={t("code", { ns: "auth" })}
                className="mb-7"
                classNameLabel="text-primary"
                required={true}
                customRules={[
                    {
                        key: "code-invalid",
                        check: (value) => isConfirmationCodeValid(value),
                        message: t("code_invalid", { ns: "auth" })
                    },
                ]}
                showErrors={showLocalErrors}
                onValidate={handleValidation}
                onChange={handleInputChange}
            />

            {/* Password */}
            <FormElement
                value={formFields.password}
                name="password"
                type="password"
                label={t("new_password", { ns: "auth" })}
                className="mb-2"
                classNameLabel="text-primary"
                passwordCreate={true}
                required={true}
                showErrors={showLocalErrors}
                onValidate={handleValidation}
                onChange={handleInputChange}
            />
            <p className="text-xs mb-7">{t("password_rules", { ns: "auth" })}</p>

            {/* Password confirmation*/}
            <FormElement
                value={formFields.passwordConfirmation}
                name="passwordConfirmation"
                type="password"
                label={t("confirm_password", { ns: "auth" })}
                className="mb-7"
                classNameLabel="text-primary"
                passwordToConfirm={formFields.password}
                required={true}
                showErrors={showLocalErrors}
                onValidate={handleValidation}
                onChange={handleInputChange}
            />

            <div className="flex justify-center mt-7">
                {formEvents.ready &&
                    <button
                        type="submit"
                        className="c-btn c-btn--secondary px-12"
                        onClick={handleSubmit}
                        style={{ maxWidth: "250px" }}
                    >
                        {t("validate", { ns: "auth" })}
                    </button>
                }
                {!formEvents.ready &&
                    <div className="c-spinner"></div>
                }
            </div>
        </form>
    )
}
